import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  AppBar,
  Toolbar,
  Container
} from '@mui/material';
import { Add, Edit, Delete, Image as ImageIcon, ArrowBack, Logout } from '@mui/icons-material';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const NewsManagement = () => {
  const { user } = useAuth();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  
  // Fonction pour obtenir l'URL correcte des fichiers uploadés
  const getFileUrl = (filePath) => {
    if (!filePath) return '';
    if (filePath.startsWith('http')) return filePath;
    // Détecter l'environnement basé sur le hostname au lieu de NODE_ENV
    const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
    const baseUrl = isProduction ? '' : 'http://localhost:3002';
    return `${baseUrl}${filePath.startsWith('/') ? filePath : '/' + filePath}`;
  };
  
  // Vérifier l'état de l'authentification
  useEffect(() => {
    const token = localStorage.getItem('token');
    console.log('Token d\'authentification:', token ? 'Présent' : 'Absent');
    console.log('Utilisateur connecté:', user ? 'Oui' : 'Non');
  }, [user]);
  const [currentNews, setCurrentNews] = useState({
    title: '',
    titleAr: '',
    content: '',
    contentAr: '',
    date: new Date().toISOString().split('T')[0],
    category: 'general',
    isPublished: true,
    image: null,
    attachments: []
  });
  const [isEditing, setIsEditing] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileLanguages, setFileLanguages] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const { logout } = useAuth();

  // Catégories d'actualités
  const categories = [
    { value: 'general', label: 'Général' },
    { value: 'formation', label: 'Formation' },
    { value: 'evenement', label: 'Événement' },
    { value: 'autre', label: 'Autre' }
  ];

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const response = await api.getNews();
      console.log('Actualités récupérées:', response.data);
      
      if (response.data && response.data.news) {
        setNews(response.data.news);
      } else if (Array.isArray(response.data)) {
        setNews(response.data);
      } else {
        setNews([]);
      }
      
      // Afficher un message de succès si des actualités ont été trouvées
      const newsCount = response.data && response.data.news ? response.data.news.length : 
                       Array.isArray(response.data) ? response.data.length : 0;
      console.log(`${newsCount} actualités récupérées avec succès`);
    } catch (error) {
      console.error('Erreur lors de la récupération des actualités:', error);
      showSnackbar('Erreur lors de la récupération des actualités', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (newsItem = null) => {
    if (newsItem) {
      // Édition d'une actualité existante
      setIsEditing(true);
      
      // Formatage de la date pour l'input de type date
      const newsDate = newsItem.date ? new Date(newsItem.date) : new Date();
      const formattedDate = newsDate instanceof Date && !isNaN(newsDate.getTime()) 
        ? newsDate.toISOString().split('T')[0] 
        : new Date().toISOString().split('T')[0];
      
      setCurrentNews({
        ...newsItem,
        date: formattedDate,
        isPublished: newsItem.isPublished !== false
      });
      
      // Prévisualisation de l'image existante
      if (newsItem.image) {
        const imageUrl = getFileUrl(newsItem.image);
        setImagePreview(imageUrl);
        console.log('URL de prévisualisation de l\'image:', imageUrl);
      } else {
        setImagePreview('');
      }
      
      // Initialiser les fichiers attachés existants
      if (newsItem.attachments && newsItem.attachments.length > 0) {
        // Initialiser les langues des fichiers
        const languages = newsItem.attachments.map(attachment => attachment.language || 'fr');
        setFileLanguages(languages);
      } else {
        setFileLanguages([]);
      }
    } else {
      // Création d'une nouvelle actualité
      setIsEditing(false);
      setCurrentNews({
        title: '',
        titleAr: '',
        content: '',
        contentAr: '',
        date: new Date().toISOString().split('T')[0],
        category: 'general',
        isPublished: true,
        image: null,
        attachments: []
      });
      setImagePreview('');
      setFileLanguages([]);
    }
    setSelectedImage(null);
    setSelectedFiles([]);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentNews({
      title: '',
      titleAr: '',
      content: '',
      contentAr: '',
      date: new Date().toISOString().split('T')[0],
      category: 'general',
      isPublished: true,
      image: null,
      attachments: []
    });
    setSelectedImage(null);
    setImagePreview('');
  };

  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;
    setCurrentNews({
      ...currentNews,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    
    // Vérifier l'authentification avant de soumettre
    const token = localStorage.getItem('token');
    console.log('Token lors de la soumission:', token ? 'Présent' : 'Absent');
    
    if (!token) {
      showSnackbar('Vous n\'êtes pas connecté. Veuillez vous reconnecter.', 'error');
      setSubmitting(false);
      navigate('/login');
      return;
    }
    
    // Validation basique
    if (!currentNews.title || !currentNews.content) {
      showSnackbar('Veuillez remplir tous les champs obligatoires', 'error');
      setSubmitting(false);
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('title', currentNews.title);
      formData.append('titleAr', currentNews.titleAr || '');
      formData.append('content', currentNews.content);
      formData.append('contentAr', currentNews.contentAr || '');
      formData.append('date', currentNews.date);
      formData.append('category', currentNews.category);
      formData.append('isPublished', currentNews.isPublished);
      
      // Ajouter l'image si elle existe
      if (selectedImage) {
        // Ajouter l'image au FormData avec le nom de champ 'image'
        formData.append('image', selectedImage);
        console.log('Image ajoutée au FormData:', selectedImage.name);
        console.log('Détails de l\'image:', {
          name: selectedImage.name,
          type: selectedImage.type,
          size: selectedImage.size,
          lastModified: new Date(selectedImage.lastModified).toISOString()
        });
        
        // Ajouter un log pour vérifier que l'image est bien dans le FormData
        const imageFile = formData.get('image');
        console.log('Vérification de l\'image dans FormData:', imageFile ? 'Présente' : 'Absente');
      } else if (currentNews.image === '') {
        // Si l'image a été supprimée
        formData.append('removeImage', true);
        console.log('Demande de suppression de l\'image');
      }
      
      // Ajouter les fichiers attachés s'ils existent
      if (selectedFiles.length > 0) {
        selectedFiles.forEach(file => {
          formData.append('attachments', file);
          console.log('Fichier ajouté au FormData:', file.name);
        });
        
        // Ajouter les langues des fichiers
        formData.append('fileLanguages', JSON.stringify(fileLanguages));
        console.log('Langues des fichiers:', fileLanguages);
      }
      
      // Si on est en mode édition et qu'on veut supprimer tous les fichiers
      if (isEditing && currentNews.attachments && currentNews.attachments.length > 0 && selectedFiles.length === 0) {
        formData.append('removeAttachments', true);
        console.log('Demande de suppression de tous les fichiers attachés');
      }
      
      // Afficher le contenu du FormData pour le débogage
      console.log('FormData envoyé:');
      for (let pair of formData.entries()) {
        console.log(pair[0] + ': ' + (pair[1] instanceof File ? pair[1].name : pair[1]));
      }
      
      let response;
      if (isEditing) {
        // Mise à jour d'une actualité existante
        response = await api.updateNews(currentNews._id, formData);
        showSnackbar('Actualité mise à jour avec succès');
      } else {
        // Création d'une nouvelle actualité
        response = await api.createNews(formData);
        showSnackbar('Actualité créée avec succès');
      }
      
      // Mettre à jour la liste des actualités
      fetchNews();
      setOpenDialog(false);
      
      // Réinitialiser les états
      setCurrentNews({
        title: '',
        titleAr: '',
        content: '',
        contentAr: '',
        date: new Date().toISOString().split('T')[0],
        category: 'general',
        isPublished: true,
        image: null,
        attachments: []
      });
      setSelectedImage(null);
      setImagePreview('');
      setSelectedFiles([]);
      setFileLanguages([]);
      
      console.log('Réponse du serveur:', response);
    } catch (error) {
      console.error('Erreur lors de la soumission:', error);
      showSnackbar('Erreur lors de la soumission. Veuillez réessayer.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteNews = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette actualité ?')) {
      try {
        await api.deleteNews(id);
        showSnackbar('Actualité supprimée avec succès', 'success');
        fetchNews();
      } catch (error) {
        console.error('Erreur lors de la suppression de l\'actualité:', error);
        showSnackbar('Erreur lors de la suppression de l\'actualité', 'error');
      }
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    
    if (file) {
      // Vérifier le type de fichier
      if (!file.type.match('image.*')) {
        showSnackbar('Veuillez sélectionner une image', 'error');
        return;
      }
      
      // Vérifier la taille du fichier (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        showSnackbar('L\'image ne doit pas dépasser 5MB', 'error');
        return;
      }
      
      console.log('Image sélectionnée:', file.name, 'Type:', file.type, 'Taille:', file.size);
      console.log('Détails de l\'image sélectionnée:', {
        name: file.name,
        type: file.type,
        size: file.size,
        lastModified: new Date(file.lastModified).toISOString()
      });
      
      setSelectedImage(file);
      
      // Prévisualisation de l'image
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        console.log('Prévisualisation de l\'image générée');
      };
      reader.readAsDataURL(file);
    }
  };
  
  // Gérer l'ajout de fichiers attachés
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length > 0) {
      // Vérifier les types de fichiers
      const validFileTypes = /pdf|doc|docx|xls|xlsx|ppt|pptx/;
      const invalidFiles = files.filter(file => {
        const fileExt = file.name.split('.').pop().toLowerCase();
        return !validFileTypes.test(fileExt);
      });
      
      if (invalidFiles.length > 0) {
        showSnackbar('Seuls les fichiers PDF, Word, Excel et PowerPoint sont autorisés', 'error');
        return;
      }
      
      // Vérifier la taille des fichiers (10MB max par fichier)
      const oversizedFiles = files.filter(file => file.size > 10 * 1024 * 1024);
      if (oversizedFiles.length > 0) {
        showSnackbar('Les fichiers ne doivent pas dépasser 10MB chacun', 'error');
        return;
      }
      
      // Limiter à 5 fichiers maximum
      if (files.length > 5) {
        showSnackbar('Vous ne pouvez pas ajouter plus de 5 fichiers', 'warning');
        const limitedFiles = files.slice(0, 5);
        setSelectedFiles(limitedFiles);
        
        // Initialiser les langues des fichiers
        setFileLanguages(limitedFiles.map(() => 'fr'));
      } else {
        setSelectedFiles(files);
        
        // Initialiser les langues des fichiers
        setFileLanguages(files.map(() => 'fr'));
      }
      
      console.log('Fichiers sélectionnés:', files.map(f => f.name).join(', '));
    }
  };
  
  // Gérer le changement de langue pour un fichier
  const handleFileLanguageChange = (index, language) => {
    const newLanguages = [...fileLanguages];
    newLanguages[index] = language;
    setFileLanguages(newLanguages);
    console.log(`Langue du fichier ${index} changée en ${language}`);
  };
  
  // Supprimer un fichier de la liste
  const handleRemoveFile = (index) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    const newLanguages = fileLanguages.filter((_, i) => i !== index);
    
    setSelectedFiles(newFiles);
    setFileLanguages(newLanguages);
    console.log(`Fichier ${index} supprimé de la liste`);
  };

  const handleBackToDashboard = () => {
    navigate('/admin/dashboard');
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="static" color="primary">
        <Toolbar>
          <IconButton 
            color="inherit" 
            edge="start" 
            onClick={handleBackToDashboard}
            sx={{ mr: 2 }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Gestion des Actualités
          </Typography>
          <Button color="inherit" onClick={handleLogout} startIcon={<Logout />}>
            Déconnexion
          </Button>
        </Toolbar>
      </AppBar>
      
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, flexGrow: 1 }}>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Liste des Actualités</Typography>
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
            >
              Ajouter une actualité
            </Button>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
              <TableContainer sx={{ maxHeight: 440 }}>
                <Table stickyHeader aria-label="sticky table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Titre</TableCell>
                      <TableCell>Image</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Catégorie</TableCell>
                      <TableCell>Statut</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {news.length > 0 ? (
                      news
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((item) => (
                          <TableRow hover role="checkbox" tabIndex={-1} key={item._id}>
                            <TableCell>{item.title}</TableCell>
                            <TableCell>
                              {item.image ? (
                                <Box
                                  component="img"
                                  sx={{
                                    height: 60,
                                    width: 60,
                                    objectFit: 'cover',
                                    borderRadius: 1
                                  }}
                                  alt={item.title}
                                  src={getFileUrl(item.image)}
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = '/image/logofr.png';
                                  }}
                                />
                              ) : (
                                <Box
                                  sx={{
                                    height: 60,
                                    width: 60,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: 'grey.200',
                                    borderRadius: 1
                                  }}
                                >
                                  <ImageIcon color="disabled" />
                                </Box>
                              )}
                            </TableCell>
                            <TableCell>
                              {item.date
                                ? new Date(item.date).toLocaleDateString('fr-FR')
                                : new Date(item.createdAt).toLocaleDateString('fr-FR')}
                            </TableCell>
                            <TableCell>
                              {categories.find(cat => cat.value === item.category)?.label || 'Général'}
                            </TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  bgcolor: item.isPublished ? 'success.light' : 'warning.light',
                                  color: 'white',
                                  py: 0.5,
                                  px: 1,
                                  borderRadius: 1,
                                  display: 'inline-block'
                                }}
                              >
                                {item.isPublished ? 'Publié' : 'Brouillon'}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <IconButton
                                color="primary"
                                onClick={() => handleOpenDialog(item)}
                                size="small"
                              >
                                <Edit />
                              </IconButton>
                              <IconButton
                                color="error"
                                onClick={() => handleDeleteNews(item._id)}
                                size="small"
                              >
                                <Delete />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          Aucune actualité trouvée
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={news.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Lignes par page :"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
              />
            </Paper>
          )}

          {/* Dialogue d'ajout/édition d'actualité */}
          <Dialog 
            open={openDialog} 
            onClose={handleCloseDialog} 
            maxWidth="md" 
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: '12px',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)'
              }
            }}
          >
            <DialogTitle sx={{ 
              backgroundColor: '#0f4c81', 
              color: 'white',
              fontWeight: 600,
              fontSize: '1.2rem',
              padding: '16px 24px'
            }}>
              {isEditing ? 'Modifier une actualité' : 'Ajouter une actualité'}
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="title"
                    label="Titre"
                    value={currentNews.title}
                    onChange={handleInputChange}
                    fullWidth
                    required
                    error={!currentNews.title && submitting}
                    helperText={!currentNews.title && submitting ? 'Le titre est requis' : ''}
                    sx={{ 
                      '& .MuiOutlinedInput-root': { 
                        '& fieldset': { borderColor: '#0f4c81' },
                        '&:hover fieldset': { borderColor: '#0f4c81' },
                        '&.Mui-focused fieldset': { borderColor: '#ffd166' }
                      },
                      '& .MuiInputLabel-root.Mui-focused': { color: '#0f4c81' }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="titleAr"
                    label="Titre (AR)"
                    value={currentNews.titleAr || ''}
                    onChange={handleInputChange}
                    fullWidth
                    dir="rtl"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="content"
                    label="Contenu"
                    value={currentNews.content}
                    onChange={handleInputChange}
                    fullWidth
                    required
                    multiline
                    rows={4}
                    error={!currentNews.content && submitting}
                    helperText={!currentNews.content && submitting ? 'Le contenu est requis' : ''}
                    sx={{ 
                      '& .MuiOutlinedInput-root': { 
                        '& fieldset': { borderColor: '#0f4c81' },
                        '&:hover fieldset': { borderColor: '#0f4c81' },
                        '&.Mui-focused fieldset': { borderColor: '#ffd166' }
                      },
                      '& .MuiInputLabel-root.Mui-focused': { color: '#0f4c81' }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="contentAr"
                    label="Contenu (AR)"
                    value={currentNews.contentAr || ''}
                    onChange={handleInputChange}
                    fullWidth
                    multiline
                    rows={4}
                    dir="rtl"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    name="date"
                    label="Date"
                    type="date"
                    value={currentNews.date}
                    onChange={handleInputChange}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={{ 
                      '& .MuiOutlinedInput-root': { 
                        '& fieldset': { borderColor: '#0f4c81' },
                        '&:hover fieldset': { borderColor: '#0f4c81' },
                        '&.Mui-focused fieldset': { borderColor: '#ffd166' }
                      },
                      '& .MuiInputLabel-root.Mui-focused': { color: '#0f4c81' }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel id="category-label">Catégorie</InputLabel>
                    <Select
                      labelId="category-label"
                      id="category"
                      name="category"
                      value={currentNews.category}
                      label="Catégorie"
                      onChange={handleInputChange}
                    >
                      {categories.map((category) => (
                        <MenuItem key={category.value} value={category.value}>
                          {category.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={currentNews.isPublished}
                        onChange={(e) => setCurrentNews({...currentNews, isPublished: e.target.checked})}
                        name="isPublished"
                        color="primary"
                      />
                    }
                    label="Publier"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Image
                    </Typography>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<ImageIcon />}
                      sx={{ 
                        mb: 2,
                        color: '#0f4c81',
                        borderColor: '#0f4c81',
                        '&:hover': {
                          borderColor: '#0a3c65',
                          backgroundColor: 'rgba(15, 76, 129, 0.1)'
                        }
                      }}
                    >
                      Sélectionner une image
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handleImageChange}
                      />
                    </Button>
                    {imagePreview && (
                      <Box sx={{ mt: 2, position: 'relative' }}>
                        <Box
                          component="img"
                          sx={{
                            width: '100%',
                            maxHeight: 300,
                            objectFit: 'contain',
                            borderRadius: 1,
                            border: '1px solid #ddd'
                          }}
                          src={imagePreview}
                          alt="Prévisualisation"
                        />
                        <IconButton
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            bgcolor: 'rgba(0, 0, 0, 0.5)',
                            color: 'white',
                            '&:hover': {
                              bgcolor: 'rgba(0, 0, 0, 0.7)'
                            }
                          }}
                          size="small"
                          onClick={() => {
                            setSelectedImage(null);
                            setImagePreview('');
                          }}
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    )}
                    {!imagePreview && currentNews.image && (
                      <Box sx={{ mt: 2, position: 'relative' }}>
                        <Box
                          component="img"
                          sx={{
                            width: '100%',
                            maxHeight: 300,
                            objectFit: 'contain',
                            borderRadius: 1,
                            border: '1px solid #ddd'
                          }}
                          src={getFileUrl(currentNews.image)}
                          alt={currentNews.title}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '/image/logofr.png';
                            console.log('Erreur de chargement de l\'image:', currentNews.image);
                          }}
                        />
                        <IconButton
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            bgcolor: 'rgba(0, 0, 0, 0.5)',
                            color: 'white',
                            '&:hover': {
                              bgcolor: 'rgba(0, 0, 0, 0.7)'
                            }
                          }}
                          size="small"
                          onClick={() => {
                            setCurrentNews({...currentNews, image: ''});
                          }}
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                </Grid>
                
                {/* Section pour les fichiers attachés */}
                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Fichiers attachés (PDF, Word, Excel, PowerPoint)
                  </Typography>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<Add />}
                    sx={{ 
                      mb: 2,
                      color: '#0f4c81',
                      borderColor: '#0f4c81',
                      '&:hover': {
                        borderColor: '#0a3c65',
                        backgroundColor: 'rgba(15, 76, 129, 0.1)'
                      }
                    }}
                  >
                    Ajouter des fichiers
                    <input
                      type="file"
                      hidden
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                      multiple
                      onChange={handleFileChange}
                    />
                  </Button>
                  
                  {/* Liste des fichiers sélectionnés */}
                  {selectedFiles.length > 0 && (
                    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Fichiers sélectionnés ({selectedFiles.length}):
                      </Typography>
                      {selectedFiles.map((file, index) => {
                        const fileExt = file.name.split('.').pop().toLowerCase();
                        let fileIcon;
                        let fileColor;
                        
                        if (fileExt === 'pdf') {
                          fileIcon = '📄';
                          fileColor = '#e53935'; // Rouge
                        } else if (['doc', 'docx'].includes(fileExt)) {
                          fileIcon = '📝';
                          fileColor = '#1565c0'; // Bleu
                        } else if (['xls', 'xlsx'].includes(fileExt)) {
                          fileIcon = '📊';
                          fileColor = '#2e7d32'; // Vert
                        } else if (['ppt', 'pptx'].includes(fileExt)) {
                          fileIcon = '📑';
                          fileColor = '#ff8f00'; // Orange
                        } else {
                          fileIcon = '📎';
                          fileColor = '#757575'; // Gris
                        }
                        
                        return (
                          <Box 
                            key={index} 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              mb: 1, 
                              p: 1,
                              borderRadius: 1,
                              border: '1px solid #e0e0e0',
                              backgroundColor: 'rgba(0, 0, 0, 0.02)'
                            }}
                          >
                            <Box sx={{ fontSize: '1.5rem', mr: 1, color: fileColor }}>{fileIcon}</Box>
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="body2" noWrap title={file.name}>
                                {file.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {(file.size / 1024 / 1024).toFixed(2)} MB
                              </Typography>
                            </Box>
                            <FormControl size="small" sx={{ minWidth: 80, mr: 1 }}>
                              <Select
                                value={fileLanguages[index] || 'fr'}
                                onChange={(e) => handleFileLanguageChange(index, e.target.value)}
                                sx={{ height: 32 }}
                              >
                                <MenuItem value="fr">Français</MenuItem>
                                <MenuItem value="ar">Arabe</MenuItem>
                                <MenuItem value="en">Anglais</MenuItem>
                              </Select>
                            </FormControl>
                            <IconButton 
                              size="small" 
                              onClick={() => handleRemoveFile(index)}
                              sx={{ color: '#d32f2f' }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                        );
                      })}
                    </Paper>
                  )}
                  
                  {/* Liste des fichiers existants (en mode édition) */}
                  {isEditing && currentNews.attachments && currentNews.attachments.length > 0 && selectedFiles.length === 0 && (
                    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Fichiers existants ({currentNews.attachments.length}):
                      </Typography>
                      {currentNews.attachments.map((attachment, index) => {
                        let fileIcon;
                        let fileColor;
                        
                        if (attachment.fileType === 'pdf') {
                          fileIcon = '📄';
                          fileColor = '#e53935'; // Rouge
                        } else if (attachment.fileType === 'word') {
                          fileIcon = '📝';
                          fileColor = '#1565c0'; // Bleu
                        } else if (attachment.fileType === 'excel') {
                          fileIcon = '📊';
                          fileColor = '#2e7d32'; // Vert
                        } else if (attachment.fileType === 'powerpoint') {
                          fileIcon = '📑';
                          fileColor = '#ff8f00'; // Orange
                        } else {
                          fileIcon = '📎';
                          fileColor = '#757575'; // Gris
                        }
                        
                        return (
                          <Box 
                            key={index} 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              mb: 1, 
                              p: 1,
                              borderRadius: 1,
                              border: '1px solid #e0e0e0',
                              backgroundColor: 'rgba(0, 0, 0, 0.02)'
                            }}
                          >
                            <Box sx={{ fontSize: '1.5rem', mr: 1, color: fileColor }}>{fileIcon}</Box>
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="body2" noWrap title={attachment.fileName}>
                                {attachment.fileName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {attachment.fileSize ? ((attachment.fileSize / 1024 / 1024).toFixed(2) + ' MB') : ''}
                                {attachment.language && ` • ${attachment.language === 'fr' ? 'Français' : attachment.language === 'ar' ? 'Arabe' : 'Anglais'}`}
                              </Typography>
                            </Box>
                            <Button 
                              size="small" 
                              variant="outlined"
                              href={getFileUrl(attachment.filePath)}
                              target="_blank"
                              sx={{ mr: 1, height: 32 }}
                            >
                              Voir
                            </Button>
                          </Box>
                        );
                      })}
                      <Button 
                        variant="outlined" 
                        color="error" 
                        size="small"
                        onClick={() => {
                          if (window.confirm('Voulez-vous vraiment supprimer tous les fichiers attachés?')) {
                            setCurrentNews({...currentNews, attachments: []});
                          }
                        }}
                        sx={{ mt: 1 }}
                      >
                        Supprimer tous les fichiers
                      </Button>
                    </Paper>
                  )}
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button 
                onClick={handleCloseDialog}
                sx={{ 
                  color: '#0f4c81',
                  '&:hover': {
                    backgroundColor: 'rgba(15, 76, 129, 0.1)'
                  }
                }}
              >
                Annuler
              </Button>
              <Button 
                onClick={handleSubmit} 
                variant="contained" 
                color="primary" 
                disabled={submitting}
                sx={{ 
                  backgroundColor: '#0f4c81',
                  '&:hover': {
                    backgroundColor: '#0a3c65'
                  }
                }}
              >
                {submitting ? <CircularProgress size={24} /> : isEditing ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Snackbar pour les notifications */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Alert
              onClose={handleCloseSnackbar}
              severity={snackbar.severity}
              sx={{ width: '100%' }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Paper>
      </Container>
    </Box>
  );
};

export default NewsManagement;
